import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import {
    WorkerPool,
    createWorkerPool,
    CSVWorkerReturn
} from "../Components/Common/Workers/workerPool"
import { JSONSchema7 } from "json-schema"
import {
    JobSubmitDataParseStatus,
    JobSubmitDataParseError
} from "../Models/Client/JobSubmitDataParseStatus"

export function useJobSubmitDataParser(
    schema: JSONSchema7,
    single: boolean
): [
    JobSubmitDataParseStatus,
    (acceptedFiles: File[], rejectedFiles?: File[]) => void,
    () => void
] {
    const attemptIDRef = useRef(0)
    const [state, setState] = useState<JobSubmitDataParseStatus>({
        type: "idle"
    })
    const [result, setResult] =
        useState<
            | { isError: false; data: CSVWorkerReturn }
            | { isError: true; error: JobSubmitDataParseError }
            | undefined
        >(undefined)
    const workers = useMemo<WorkerPool<"CSV_WORKER">>(
        () => createWorkerPool(1),
        []
    )
    const handleFileDrop = useCallback(
        async (acceptedFiles: File[], rejectedFiles: File[] = []) => {
            const rejected = rejectedFiles.length > 0
            const file = rejected ? rejectedFiles[0] : acceptedFiles[0]
            const fileType = file.type === "application/json" ? "json" : "csv"
            const attemptID = ++attemptIDRef.current

            if (!file) return

            if (file.size > 50 * 1000 * 1000) {
                setState({
                    type: "failed",
                    fileName: file.name,
                    error: { type: "too_big" },
                    fileType
                })
                return
            }

            if (rejected) {
                setState({
                    type: "failed",
                    fileName: file.name,
                    error: { type: "format" },
                    fileType
                })
                return
            }

            setState({
                type: "inProgress",
                fileName: file.name,
                progress: 0,
                size: file.size,
                fileType
            })
            setResult(undefined)

            try {
                const result = await workers.call("CSV_WORKER", {
                    file,
                    schema,
                    uploadSizeThreshold: 3 * 1000 * 1000, // use uploading if serialized JSON is larger than 3MB
                    maxReportErrorItemCount: 100,
                    single
                })

                // Ignore result if attemptID doesn't match, in order to avoid overwriting result of the last attempt
                if (attemptID === attemptIDRef.current) {
                    setResult({ isError: false, data: result })
                }
            } catch (error) {
                if (attemptID !== attemptIDRef.current) {
                    return
                }

                switch ((error as any).name) {
                    case "CSVError":
                    case "SchemaValidationError":
                        setResult({
                            isError: true,
                            error: { type: "validation", error: error as any }
                        })
                        break
                    case "GenericError":
                        setResult({
                            isError: true,
                            error: {
                                type: "other",
                                message: (error as any).message
                            }
                        })
                        break
                    default:
                        setResult({ isError: true, error: { type: "format" } })
                }
            }
        },
        [single, schema, workers]
    )
    const handleReset = useCallback(() => setState({ type: "idle" }), [])

    useEffect(() => {
        return () => {
            if (workers !== undefined) {
                workers.deactivate()
            }
        }
    }, [workers])

    useEffect(() => {
        if (state.type !== "inProgress") {
            return
        }

        const progress = state.progress

        if (result !== undefined) {
            if (progress === 0) return
            if (progress >= 1) {
                setState(
                    result.isError
                        ? {
                              type: "failed",
                              error: result.error,
                              fileName: state.fileName,
                              fileType: state.fileType
                          }
                        : {
                              type: "parsed",
                              data: result.data.data,
                              fileName: state.fileName,
                              fileType: result.data.isJSONFile ? "json" : "csv",
                              messages: result.data.messages
                          }
                )
                setResult(undefined)
                return
            }
            const clear = window.setTimeout(() => {
                setState({ ...state, progress: progress + 0.02 })
            }, 10)

            return () => window.clearTimeout(clear)
        }

        if (progress === 0) {
            setState({ ...state, progress: 0.001 })
            return
        }

        const estimatedTime = (state.size / 10000000) * 60 * 1000
        const clear = window.setTimeout(() => {
            setState({
                ...state,
                progress:
                    progress < 0.9
                        ? progress + 0.01
                        : progress >= 0.98
                        ? 0.98
                        : progress + 0.0001
            })
        }, estimatedTime * 0.001)
        return () => clearTimeout(clear)
    }, [result, state])

    return [state, handleFileDrop, handleReset]
}
